import type { TripListRowType } from './tripListTypes'

export type TripListPlaceCategory =
  | 'restaurant'
  | 'cafe'
  | 'bar'
  | 'park'
  | 'museum'
  | 'shopping'
  | 'sport'
  | 'flight'
  | 'transport'
  | 'lodging'
  | 'cruise'
  | 'attraction'

interface TripListPlaceCategoryInput {
  primaryType?: string
  types?: ReadonlyArray<string>
  primaryTypeDisplayName?: string
  fallbackText?: string
}

const BAR_GOOGLE_TYPES: ReadonlySet<string> = new Set([
  'bar',
  'bar_and_grill',
  'night_club',
  'pub',
  'wine_bar',
])
const CAFE_GOOGLE_TYPES: ReadonlySet<string> = new Set([
  'bagel_shop',
  'bakery',
  'cafe',
  'cafeteria',
  'coffee_shop',
  'confectionery',
  'dessert_shop',
  'donut_shop',
  'ice_cream_shop',
  'juice_shop',
  'tea_house',
])
const FLIGHT_GOOGLE_TYPES: ReadonlySet<string> = new Set(['airport'])
const LODGING_GOOGLE_TYPES: ReadonlySet<string> = new Set([
  'bed_and_breakfast',
  'guest_house',
  'hostel',
  'hotel',
  'lodging',
  'motel',
])
const MUSEUM_GOOGLE_TYPES: ReadonlySet<string> = new Set(['museum', 'art_gallery'])
const PARK_GOOGLE_TYPES: ReadonlySet<string> = new Set(['park', 'national_park'])
const RESTAURANT_GOOGLE_TYPES: ReadonlySet<string> = new Set([
  'deli',
  'diner',
  'food_court',
  'meal_delivery',
  'meal_takeaway',
  'restaurant',
  'sandwich_shop',
  'steak_house',
])
const SHOPPING_GOOGLE_TYPES: ReadonlySet<string> = new Set([
  'shopping_mall',
  'clothing_store',
  'department_store',
])
const SPORT_GOOGLE_TYPES: ReadonlySet<string> = new Set(['stadium', 'sports_complex', 'golf_course'])
const TRANSPORT_GOOGLE_TYPES: ReadonlySet<string> = new Set([
  'bus_station',
  'ferry_terminal',
  'light_rail_station',
  'subway_station',
  'taxi_stand',
  'train_station',
  'transit_station',
])

const BAR_DISPLAY_NAMES = ['bar', 'pub', 'cocktail bar', 'wine bar', 'brewery', 'nightclub']
const CAFE_DISPLAY_NAMES = ['cafe', 'coffee shop', 'bakery', 'tea house']
const CRUISE_DISPLAY_NAMES = ['cruise']
const FLIGHT_DISPLAY_NAMES = ['airport']
const LODGING_DISPLAY_NAMES = ['hotel', 'lodging', 'hostel', 'motel']
const MUSEUM_DISPLAY_NAMES = ['museum', 'art gallery']
const PARK_DISPLAY_NAMES = ['park', 'garden', 'botanical garden', 'national park']
const RESTAURANT_DISPLAY_NAMES = ['restaurant', 'seafood restaurant', 'family restaurant']
const SHOPPING_DISPLAY_NAMES = ['shopping mall', 'market', 'store', 'department store']
const SPORT_DISPLAY_NAMES = ['stadium', 'sports complex', 'golf course']
const TRANSPORT_DISPLAY_NAMES = ['train station', 'bus station', 'ferry terminal', 'subway station', 'transit station', 'taxi stand']

const CRUISE_FALLBACK_KEYWORDS = ['cruise']
const FLIGHT_FALLBACK_KEYWORDS = ['flight', 'airport', 'airline']
const TRANSPORT_FALLBACK_KEYWORDS = ['train', 'bus', 'ferry', 'driving', 'drive', 'transport', 'transfer', 'subway', 'transit', 'taxi']

function hasKeyword(text: string, keywords: ReadonlyArray<string>): boolean {
  const paddedText = ` ${text.replace(/[^a-z0-9]+/g, ' ')} `
  return keywords.some(keyword => paddedText.includes(` ${keyword} `))
}

function getCategoryForGoogleType(
  googleType: string | undefined,
  includeGenericTypes: boolean,
): TripListPlaceCategory | undefined {
  if (!googleType) return undefined

  if (BAR_GOOGLE_TYPES.has(googleType)) {
    return 'bar'
  }

  if (CAFE_GOOGLE_TYPES.has(googleType)) {
    return 'cafe'
  }

  if (
    RESTAURANT_GOOGLE_TYPES.has(googleType) ||
    googleType.endsWith('_restaurant') ||
    (includeGenericTypes && googleType === 'food')
  ) {
    return 'restaurant'
  }

  if (PARK_GOOGLE_TYPES.has(googleType)) {
    return 'park'
  }

  if (
    SHOPPING_GOOGLE_TYPES.has(googleType) ||
    googleType.endsWith('_store') ||
    (includeGenericTypes && googleType === 'store')
  ) {
    return 'shopping'
  }

  if (MUSEUM_GOOGLE_TYPES.has(googleType)) {
    return 'museum'
  }

  if (SPORT_GOOGLE_TYPES.has(googleType)) {
    return 'sport'
  }

  if (FLIGHT_GOOGLE_TYPES.has(googleType)) {
    return 'flight'
  }

  if (TRANSPORT_GOOGLE_TYPES.has(googleType)) {
    return 'transport'
  }

  if (LODGING_GOOGLE_TYPES.has(googleType)) {
    return 'lodging'
  }

  return undefined
}

function getCategoryForGoogleTypes(types: ReadonlyArray<string> | undefined): TripListPlaceCategory | undefined {
  if (!types) return undefined

  return types.reduce<TripListPlaceCategory | undefined>(
    (category, googleType) => category ?? getCategoryForGoogleType(googleType, false),
    undefined,
  )
}

function getCategoryForText(text: string): TripListPlaceCategory | undefined {
  if (hasKeyword(text, BAR_DISPLAY_NAMES)) return 'bar'
  if (hasKeyword(text, CAFE_DISPLAY_NAMES)) return 'cafe'
  if (hasKeyword(text, RESTAURANT_DISPLAY_NAMES)) return 'restaurant'
  if (hasKeyword(text, PARK_DISPLAY_NAMES)) return 'park'
  if (hasKeyword(text, SHOPPING_DISPLAY_NAMES)) return 'shopping'
  if (hasKeyword(text, MUSEUM_DISPLAY_NAMES)) return 'museum'
  if (hasKeyword(text, SPORT_DISPLAY_NAMES)) return 'sport'
  if (hasKeyword(text, FLIGHT_DISPLAY_NAMES)) return 'flight'
  if (hasKeyword(text, TRANSPORT_DISPLAY_NAMES)) return 'transport'
  if (hasKeyword(text, LODGING_DISPLAY_NAMES)) return 'lodging'
  if (hasKeyword(text, CRUISE_DISPLAY_NAMES)) return 'cruise'

  return undefined
}

function getCategoryForFallbackText(text: string): TripListPlaceCategory | undefined {
  if (hasKeyword(text, FLIGHT_FALLBACK_KEYWORDS)) return 'flight'
  if (hasKeyword(text, TRANSPORT_FALLBACK_KEYWORDS)) return 'transport'
  if (hasKeyword(text, CRUISE_FALLBACK_KEYWORDS)) return 'cruise'

  return undefined
}

export function getTripListPlaceCategory(input: TripListPlaceCategoryInput): TripListPlaceCategory {
  const primaryTypeCategory = getCategoryForGoogleType(input.primaryType, true)
  if (primaryTypeCategory) {
    return primaryTypeCategory
  }

  const typesCategory = getCategoryForGoogleTypes(input.types)
  if (typesCategory) {
    return typesCategory
  }

  const textCategory = getCategoryForText(input.primaryTypeDisplayName?.toLowerCase() ?? '')
  if (textCategory) {
    return textCategory
  }

  const fallbackTextCategory = getCategoryForFallbackText(input.fallbackText?.toLowerCase() ?? '')
  if (fallbackTextCategory) {
    return fallbackTextCategory
  }

  return 'attraction'
}

export function getTripListRowTypeFromPlaceCategory(category: TripListPlaceCategory): TripListRowType {
  switch (category) {
    case 'restaurant':
    case 'cafe':
    case 'bar':
      return 'dining'
    case 'flight':
      return 'flight'
    case 'transport':
      return 'transport'
    case 'lodging':
      return 'accommodation'
    case 'cruise':
      return 'cruise'
    case 'park':
    case 'museum':
    case 'shopping':
    case 'sport':
    case 'attraction':
      return 'activity'
    default:
      return 'activity'
  }
}
